import * as storage from '@/common/storage.js';
import { useMoinBot } from "@/services/bot/botv2";
import type { AIMessageOptions, Botconfig, Intent } from '@/types';
import { defineStore } from "pinia";
import { computed, readonly, ref } from "vue";
import { useLayoutStore } from "./layout";
import logger from "@/logger";
import signals from '@/common/signals';
import { parseInputType } from '@/services/bot/helper';
import { useTeaserStore } from './teaser';
import { useI18n } from '@/composables/useI18n';

const WIDGET_STATES = {
  OPEN: 'open',
  CLOSED: 'closed',
  STARTSCREEN: 'startscreen',
} as const;

type ObjectValues<T> = T[keyof T];
type WIDGET_STATE = ObjectValues<typeof WIDGET_STATES>;

const moinBot = useMoinBot();

export const useWidgetStore = defineStore('widget', () => {
  const teaser = useTeaserStore();
  const layout = useLayoutStore();
  const { mergeLocaleMessage, mergeLocaleMessages } = useI18n();

  const hasInputFocus = ref(true);

  const config = ref<Botconfig>()
  const isScrollLockAllowed = ref(false);
  const showPoweredBy = ref(true);
  const showHeader = ref(true);
  const cacheQuickReplyIntent = ref<Intent | null>();
  const behaviour = ref({ widgetOpen: 'restorePrevious', });

  const copytext = ref({
    'textinput.placeholder': 'Nachricht senden',
    'header.name': '',
  })


  const showWidgetHeaderCloseButton = computed(() => {
    return !(behaviour.value.widgetOpen === 'always')
  })

  const setWidgetOpenBehaviour = (value: string) => {
    behaviour.value.widgetOpen = value;
  }

  const aiIndicator = ref<AIMessageOptions>({
    enabled: true,
    generated: 'Generated',
    sources: 'basiert auf',
    disclaimer:
      'KI generierte Texte können Fehler enthalten. Überprüfe wichtige Informationen.',
    pages: 'Pages',
  });



  const inputCharacterLimit = ref(256);

  // ##############################
  // Visibility
  // ##############################

  const isVisible = ref(true);
  const setVisible = (visible: boolean) => {
    isVisible.value = visible;
  };

  // ##############################
  // Startscreen related
  // ##############################
  const startScreen = ref();
  const showStartscreen = computed(() => widgetState.value === 'startscreen')
  const getStartscreen = computed(() => startScreen.value)

  const acceptStartscreen = async () => {
    // the bot can't be connected here, because the startscreen is only shown if the bot is not connected
    if (cacheQuickReplyIntent.value) {
      await moinBot.sendIntent(cacheQuickReplyIntent.value);
    } else {
      await moinBot.openConnection();
    }
    setWidgetState(WIDGET_STATES.OPEN);
  };

  // ##############################
  // Widget State
  // ##############################
  const widgetState = ref<WIDGET_STATE>(WIDGET_STATES.CLOSED);


  const isWidgetOpen = computed(() => widgetState.value === 'open' || widgetState.value === 'startscreen')
  const setWidgetState = (value: WIDGET_STATE) => {
    widgetState.value = value;
    // make sure we save the state consistant in the localstorage
    if (value === WIDGET_STATES.OPEN || value === WIDGET_STATES.STARTSCREEN) {
      signals.interaction('widget', { action: 'open_widget' });
      storage.write('knowhere_widget_open', 'open');
    }
    if (value === WIDGET_STATES.CLOSED) {
      signals.interaction('widget', { action: 'close_widget' });
      storage.write('knowhere_widget_open', 'close');
    }
  };

  const openWidget = (intent?: Intent) => {
    // If we have a startscreen and no JWT token, we show the startscreen
    // and cache the intent to send it after the startscreen is accepted
    const showStartscreen = getStartscreen.value && !moinBot.hasJWTToken();
    teaser.hideTeaser();
    if (showStartscreen) {
      cacheQuickReplyIntent.value = intent;
      setWidgetState(WIDGET_STATES.STARTSCREEN);
      return;
    }

    if (!moinBot.hasConnection() && !intent) {
      console.log('open widget without intent');
      // if the bot is not connected and we got no intent, we open the connection
      moinBot.openConnection()
    }
    if (!moinBot.hasConnection() && intent) {
      console.log('open widget WITH intent', intent);
      // If we got an intent and the bot is not connected, we send the intent
      // sendIntent will open the connection if it's not open
      moinBot.sendIntent(intent);
    }

    // FINALLY OPEN WIDGET
    setWidgetState(WIDGET_STATES.OPEN);
  }
  const closeWidget = () => setWidgetState(WIDGET_STATES.CLOSED);
  const toggleWidget = () => isWidgetOpen.value ? closeWidget() : openWidget();

  // ##############################
  // Initialization
  // ##############################

  type AIIndicatorOptions = {
    enabled?: boolean;
    generated?: string;
    disclaimer?: string;
    sources?: string;
    pages?: string;
  }
  const setAIIndicator = (options: AIIndicatorOptions = {}) => {
    if ('enabled' in options) {
      aiIndicator.value.enabled = options.enabled;
    }
    if (options.generated) {
      aiIndicator.value.generated = options.generated;
      mergeLocaleMessage('message.ai.generated', options.generated);
    }
    if (options.disclaimer) {
      aiIndicator.value.disclaimer = options.disclaimer;
      mergeLocaleMessage('message.ai.disclaimer', options.disclaimer);
    }
    if (options.sources) {
      aiIndicator.value.sources = options.sources;
      mergeLocaleMessage('message.ai.sources', options.sources);
    }
    if (options.pages) {
      aiIndicator.value.pages = options.pages;
      mergeLocaleMessage('message.ai.pages', options.pages);
    }
  }

  const setConfig = (botconfig: Botconfig) => {
    // set the config
    if (!botconfig) return;
    config.value = { ...config.value, ...botconfig };
    behaviour.value = botconfig.behaviour;
    setAIIndicator(botconfig.aiIndicator);
    isScrollLockAllowed.value = botconfig.useScrollLock ?? false;
    showPoweredBy.value = botconfig.poweredBy ?? true;
    startScreen.value = botconfig?.components?.widget?.startscreen;

    mergeLocaleMessage('ratingbar.label', botconfig?.rating?.label || 'Rate conversation');
    mergeLocaleMessage('header.name', botconfig?.components?.widget?.header?.name);
    mergeLocaleMessage('header.subtitle', botconfig?.components?.widget?.operator?.default?.titel);
    mergeLocaleMessage('textinput.placeholder', botconfig.components?.widget?.input?.placeholder);
    // the i18n object has presedent over the botconfig, so i18n can override the botconfig
    if (botconfig?.i18n && typeof botconfig.i18n === 'object') {
      mergeLocaleMessages(botconfig.i18n);
    }
    inputCharacterLimit.value = botconfig.components?.widget?.input?.characterLimit ?? 256;
    moinBot.requestInputType(parseInputType(botconfig?.components?.widget?.input?.inputType));
  }
  const initializeFromConfig = (config: Botconfig) => {
    switch (config?.behaviour?.widgetOpen) {
      case 'restorePrevious': {
        if (!config.useLocalStorage) {
          console.warn('restorePrevious is set but localstorage not allowed');
          return;
        };
        const lastWidgetState = storage.readAsString('knowhere_widget_open');
        logger.log('restore last widget state', lastWidgetState);
        // we only restore the widget state if it was open and we are not on mobile
        // we start in closed state by default
        if (lastWidgetState === 'open' && !layout.isMobile) {
          openWidget();
        }
      } break;
      case 'open': openWidget(); break;
      case 'always': openWidget(); break;
      case 'close': closeWidget(); break;
      default: console.warn('widget restore mode not configured correctly');
    }
  }
  const initializeFromScriptTagAttributes = (options: string[]) => {
    // data attrs will be camelcased
    // data-force-open will become forceOpen
    // possible options:
    // data-force-open -> forceOpen
    // data-force-fullscreen -> forceFullscreen
    // data-force-noclosebutton -> forceNoclosebutton
    // data-hide-header -> hideHeader
    // data-no-inputfocus -> noInputfocus

    if (options.includes('forceOpen')) openWidget();
    if (options.includes('forceFullscreen')) {
      layout.setForceFullscreen(true);
    }
    if (options.includes('forceNoclosebutton')) behaviour.value = { widgetOpen: 'always' };
    if (options.includes('hideHeader')) showHeader.value = false;
    if (options.includes('noInputfocus')) hasInputFocus.value = false;

  }





  return {
    widgetState,
    hasInputFocus: readonly(hasInputFocus),
    aiIndicator: readonly(aiIndicator),
    isScrollLockAllowed: readonly(isScrollLockAllowed),
    inputCharacterLimit: readonly(inputCharacterLimit),

    // this is not good because we rerender all comps
    // we need a i18n plugin anyway
    // so refactor it later
    getCopy: readonly(copytext),

    setWidgetOpenBehaviour,
    showStartscreen,
    getStartscreen,
    acceptStartscreen,

    initializeFromScriptTagAttributes,
    initializeFromConfig,
    setConfig,

    showPoweredBy: readonly(showPoweredBy),
    isWidgetOpen,
    isVisible: readonly(isVisible),
    openWidget,
    closeWidget,
    toggleWidget,
    setVisible,
    showWidgetHeaderCloseButton,

    showHeader: readonly(showHeader),

  }
})
